import { Eye } from "lucide-react";
import { FormInput } from "../../../../../../components/ui/Input/FormInput/FormInput";
import { FormSearchSelect } from "../../../../../../components/ui/Input/FormSearchSelect/FormSearchSelect";
import { FormSelect } from "../../../../../../components/ui/Input/FormSelector/FormSelect";
import { FormSelectWithButton } from "../../../../../../components/ui/Input/FormSelectWithButton/FormSelectWithButton";
import { Button } from "../../../../../../components/ui/common/button";
import { dataTypeOptions, storageEnvTypeOptions } from "../../../../../../config/constants";
import { validateCharacterLength } from "../../../../../../utils/utils";
import {
  ExternalStorageSearchFacilityOption,
  ExternalStorageSectionProps,
  ExternalStorageSelectOption,
  ExternalStorageVariantFeatures
} from "../externalStorage.types";

interface ExternalStorageDetailsSectionProps extends ExternalStorageSectionProps {
  variantConfig: ExternalStorageVariantFeatures;
  facilities: ExternalStorageSelectOption[];
  searchFacilities: ExternalStorageSearchFacilityOption[];
  facilityLoading: boolean;
  onFacilityChange: (value: string) => void;
  onViewFacility: () => void;
  nameDisabled?: boolean;
  selectedFacilityId?: string;
}

const WarehouseViewButton = ({
  disabled,
  onClick
}: {
  disabled?: boolean;
  onClick: () => void;
}) => (
  <Button
    type="button"
    variant="outline"
    size="lg"
    onClick={onClick}
    disabled={disabled}
    className="tw-h-10 tw-shrink-0 tw-gap-1.5 tw-px-4 tw-border-border tw-font-medium tw-text-foreground hover:tw-bg-muted hover:tw-text-foreground"
  >
    <Eye className="tw-h-4 tw-w-4" />
    View
  </Button>
);

const ExternalStorageDetailsSection = ({
  control,
  disabled,
  variantConfig,
  facilities,
  searchFacilities,
  facilityLoading,
  onFacilityChange,
  onViewFacility,
  nameDisabled = false,
  selectedFacilityId
}: ExternalStorageDetailsSectionProps) => (
  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
    <FormInput
      control={control}
      name="externalStorageName"
      label="External Storage Name"
      placeholder="External Storage Name"
      required
      disabled={disabled || nameDisabled}
      errorMessage="External Storage Name is required"
      rules={{
        validate: (value: string) =>
          validateCharacterLength(
            value,
            32,
            "External Storage Name cannot exceed 32 characters"
          ) || true
      }}
    />

    {variantConfig.supportsDataType && (
      <FormSelect
        control={control}
        name="dataType"
        label="Data Type"
        placeholder="Select Data Type"
        options={dataTypeOptions}
        required
        disabled={disabled}
        errorMessage="Data Type is required"
      />
    )}

    {variantConfig.supportsStorageEnvironment && (
      <FormSelect
        control={control}
        name="storageEnv"
        label="Storage Environment"
        placeholder="Select Storage Environment"
        options={storageEnvTypeOptions}
        required
        disabled={disabled}
        errorMessage="Storage Environment is required"
      />
    )}

    {variantConfig.usesSearchFacility ? (
      <div className="tw-flex tw-items-end tw-gap-2">
        <div className="tw-flex-1 tw-min-w-0">
          <FormSearchSelect
            control={control}
            name="warehouseName"
            label="Warehouse Name"
            placeholder="Select Warehouse Name"
            options={searchFacilities}
            required
            disabled={disabled}
            loading={facilityLoading}
            errorMessage="Warehouse name is required"
            onChange={onFacilityChange}
          />
        </div>
        {variantConfig.supportsFacilityView && (
          <WarehouseViewButton
            disabled={disabled || !selectedFacilityId}
            onClick={onViewFacility}
          />
        )}
      </div>
    ) : (
      <FormSelectWithButton
        control={control}
        name="warehouseName"
        label="Warehouse Name"
        placeholder="Select Warehouse Name"
        options={facilities}
        required
        disabled={disabled || facilityLoading}
        errorMessage="Warehouse name is required"
        onChange={onFacilityChange}
        buttonTitle="View"
        buttonOnClick={onViewFacility}
        buttonDisabled={disabled || !selectedFacilityId}
      />
    )}
  </div>
);

export default ExternalStorageDetailsSection;
